import React from 'react';

import {
  CartesianGrid,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { RECHARTS_COLORS } from '../../../constants/charts.js';
import {
  CHART_COLORS_IN_ORDER_TAILWIND,
  colorizeData,
  computeScatterDomain,
  formatLabel,
} from '../../../helpers/charts.js';
import type { ChartProps } from '../../../types/charts.js';
import ScatterLegend from './ScatterLegend.jsx';
import ScatterTooltip from './ScatterTooltip.jsx';

const CustomScatterPlot = ({
  x,
  y,
  label,
  dataframe,
  isMobile,
  isAgentic,
  isExport,
}: ChartProps) => {
  const isCompactView = isMobile || isAgentic;

  const colorizedData = colorizeData(
    dataframe,
    CHART_COLORS_IN_ORDER_TAILWIND,
    label,
  );

  // Adjust axis domain for better spacing on mobile
  const xDomain = isCompactView
    ? computeScatterDomain(dataframe, x, 0.2, 0.9)
    : ['auto', 'auto'];

  const yDomain = isCompactView
    ? computeScatterDomain(dataframe, y[0], 0.3, 0.8)
    : ['auto', 'auto'];

  const Chart = (
    <ScatterChart
      width={700}
      height={isCompactView ? 650 : 500}
      margin={{ top: 10, right: 30, bottom: 30, left: 30 }}
    >
      <CartesianGrid stroke={RECHARTS_COLORS.BORDER_SECONDARY} />
      <XAxis
        fontSize={12}
        type="number"
        dataKey={x}
        name={x}
        domain={xDomain}
        label={{
          value: formatLabel(x),
          position: 'insideBottom',
          fill: RECHARTS_COLORS.TEXT_PRIMARY,
          offset: -10,
        }}
      />
      <YAxis
        fontSize={12}
        type="number"
        dataKey={y[0]}
        name={y[0]}
        domain={yDomain}
        label={{
          value: formatLabel(y[0]),
          angle: -90,
          fill: RECHARTS_COLORS.TEXT_PRIMARY,
          position: 'insideLeft',
          offset: -10,
        }}
      />
      <Scatter
        data={colorizedData}
        lineType="fitting"
        shape="circle"
        r={isCompactView ? 3 : 6}
        isAnimationActive={!isExport}
      />
      <Tooltip cursor={{ strokeDasharray: '3 3' }} content={ScatterTooltip} />
    </ScatterChart>
  );

  if (isCompactView) {
    return (
      <>
        <ResponsiveContainer width="99%" height={isCompactView ? 650 : 500}>
          {Chart}
        </ResponsiveContainer>
        <ScatterLegend
          label={label}
          dataframe={dataframe}
          yKey={y[0]}
          xKey={x}
        />
      </>
    );
  }

  return (
    <div className="mb-2 flex flex-col items-center justify-center">
      {Chart}
      <ScatterLegend label={label} dataframe={dataframe} yKey={y[0]} xKey={x} />
    </div>
  );
};

export default CustomScatterPlot;
